interface DisabledUntil {
  conversationId: string;
  timestamp: number;
}

export const THIRTY_MINUTES = 1000 * 60 * 30;
const TWENTY_FOUR_HOURS = 1000 * 60 * 60 * 24;
export const THREE_HOURS = 1000 * 60 * 60 * 3;
export const ONE_HOUR = 1000 * 60 * 60;

const getDisabledUntilKey = (apiKey: string) => `ft-disable-${apiKey}-until`;

const isTimestampWithinBoundary = (timestamp: number, boundary: number) => {
  return timestamp > Date.now() && timestamp < Date.now() + boundary;
};

export const setDisabledUntil = ({
  apiKey,
  conversationId,
  ms = THIRTY_MINUTES,
}: {
  apiKey: string;
  conversationId: string;
  ms: number;
}) => {
  try {
    localStorage.setItem(
      getDisabledUntilKey(apiKey),
      JSON.stringify({
        conversationId,
        timestamp: Date.now() + ms,
      }),
    );
  } catch {
    // Continue regardless of error
  }
};

export const getDisabledUntil = (apiKey: string) => {
  try {
    const disabledUntil = localStorage.getItem(getDisabledUntilKey(apiKey));

    if (!disabledUntil) {
      return null;
    }

    const parsedDisabledUntil = JSON.parse(disabledUntil) as DisabledUntil;

    // TODO: runtime type checking with zod would be nice here
    if (
      'conversationId' in parsedDisabledUntil &&
      'timestamp' in parsedDisabledUntil &&
      !isNaN(Number(parsedDisabledUntil.timestamp))
    ) {
      return parsedDisabledUntil;
    }

    return null;
  } catch {
    return null;
  }
};

const removeDisabledUntil = (apiKey: string) => {
  try {
    localStorage.removeItem(getDisabledUntilKey(apiKey));
  } catch {
    // Continue regardless of error
  }
};

export const isForethoughtDisabledByLocalStorage = (apiKey: string) => {
  const disabledUntil = getDisabledUntil(apiKey);

  if (!disabledUntil) {
    removeDisabledUntil(apiKey);
    return false;
  }

  // To prevent local storage key being disabled indefinitely
  const isDisabled = isTimestampWithinBoundary(
    disabledUntil.timestamp,
    TWENTY_FOUR_HOURS,
  );

  if (!isDisabled) {
    removeDisabledUntil(apiKey);
  }

  return isDisabled;
};
